import React from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Target,
  CheckCircle,
  Clock,
  Award,
  Wallet,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SalesPerformance, DashboardPeriod } from '@/types/salesDashboard';

// Format currency in Indian format
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

interface MetricItemProps {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subValue?: string;
  iconColor: string;
  delay?: number;
  onClick?: () => void;
  tooltip?: string;
}

const MetricItem: React.FC<MetricItemProps> = ({
  icon: Icon,
  label,
  value,
  subValue,
  iconColor,
  delay = 0,
  onClick,
  tooltip,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay }}
      className={`bg-white rounded-lg border border-gray-100 p-4 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer hover:border-gray-300' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      aria-label={onClick ? `${label}: ${value}. Click to view details.` : undefined}
      title={tooltip}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${iconColor}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-500 truncate">{label}</p>
          <p className="text-lg font-semibold text-gray-900">{value}</p>
          {subValue && (
            <p className="text-xs text-gray-400">{subValue}</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

interface SalesPerformanceGridProps {
  performance: SalesPerformance;
  period: DashboardPeriod;
  isLoading?: boolean;
  onMetricClick?: (key: string) => void;
}

export const SalesPerformanceGrid: React.FC<SalesPerformanceGridProps> = ({
  performance,
  period,
  isLoading,
  onMetricClick,
}) => {
  const periodLabel = {
    today: 'Today',
    week: 'This Week',
    month: 'This Month',
  }[period];

  // Avg Deal Size (MTD) = Bookings (salesPeriod, when period=month) / dealsWonMonth
  const avgDealSize =
    performance.dealsWonMonth > 0
      ? performance.salesPeriod / performance.dealsWonMonth
      : 0;

  const metrics: Array<{
    key: string;
    icon: React.ElementType;
    label: string;
    value: string | number;
    subValue?: string;
    iconColor: string;
    tooltip?: string;
  }> = [
    {
      key: 'avgDealSize',
      icon: TrendingUp,
      label: 'Avg Deal Size (MTD)',
      value: formatCurrency(avgDealSize),
      subValue: `${performance.dealsWonMonth} deals won`,
      iconColor: 'bg-blue-500',
      tooltip: 'Average booked deal value this month = Bookings (MTD) / Deals Won (MTD).',
    },
    {
      key: 'periodTarget',
      icon: Target,
      label: `${periodLabel}'s Target`,
      value: formatCurrency(performance.periodTarget),
      iconColor: 'bg-purple-500',
    },
    {
      key: 'targetAchieved',
      icon: CheckCircle,
      label: 'Target Achieved (MTD)',
      value: formatCurrency(performance.targetAchieved),
      iconColor: 'bg-green-500',
      tooltip: 'Same formula as Bookings — your month\'s target attainment is measured by booked deals.',
    },
    {
      key: 'remainingTarget',
      icon: Clock,
      label: 'Remaining Target',
      value: formatCurrency(performance.remainingTarget),
      iconColor: 'bg-orange-500',
    },
    {
      key: 'dealsWon',
      icon: Award,
      label: `Deals Won ${periodLabel}`,
      value: performance.dealsWon.toString(),
      subValue: `${performance.dealsWonMonth} this month`,
      iconColor: 'bg-emerald-500',
    },
    {
      key: 'pipelineValue',
      icon: Wallet,
      label: 'Total Pipeline Value',
      value: formatCurrency(performance.pipelineValue),
      iconColor: 'bg-indigo-500',
    },
  ];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sales Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="bg-gray-100 rounded-lg h-24 animate-pulse"
              />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          Sales Performance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {metrics.map((metric, index) => (
            <MetricItem
              key={metric.key}
              icon={metric.icon}
              label={metric.label}
              value={metric.value}
              subValue={metric.subValue}
              iconColor={metric.iconColor}
              delay={index * 0.05}
              onClick={onMetricClick ? () => onMetricClick(metric.key) : undefined}
              tooltip={metric.tooltip}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
